const DB_URL = "https://shopify-likes-e0091-default-rtdb.asia-southeast1.firebasedatabase.app";
const customerId = window.__shopify_customer_id || null;

function formatCount(count) {
  if (count < 1000) return count;
  return Math.floor(count / 1000) + 'k';
}

// ============================================================
// 全商品分のいいね数 / 自分がいいね済みの商品一覧は、
// ページ内で1回だけ取得してキャッシュを共有する。
// いいね操作が起きた際は、このキャッシュ自体も直接更新することで、
// 並び替えなどによる再初期化が起きても状態が正しく保たれるようにする。
// ============================================================
let allLikesCache = null;
let allLikesPromise = null;
function fetchAllLikesOnce() {
  if (allLikesCache) return Promise.resolve(allLikesCache);
  if (!allLikesPromise) {
    allLikesPromise = fetch(`${DB_URL}/likes.json`)
      .then(async res => {
        if (!res.ok) {
          const detail = await res.text().catch(() => '');
          console.error(`いいね数一覧の取得に失敗しました (status: ${res.status}) ${detail}`);
          return {};
        }
        return res.json();
      })
      .then(val => {
        allLikesCache = val || {};
        return allLikesCache;
      })
      .catch(() => {
        allLikesCache = {};
        return allLikesCache;
      });
  }
  return allLikesPromise;
}

let likedByMeCache = null;
let likedByMePromise = null;
function fetchLikedByMeOnce() {
  if (!customerId) return Promise.resolve({});
  if (likedByMeCache) return Promise.resolve(likedByMeCache);
  if (!likedByMePromise) {
    likedByMePromise = fetch(`${DB_URL}/liked/${customerId}.json`)
      .then(async res => {
        if (!res.ok) {
          const detail = await res.text().catch(() => '');
          console.error(`いいね済み一覧の取得に失敗しました (status: ${res.status}) ${detail}`);
          return {};
        }
        return res.json();
      })
      .then(val => {
        likedByMeCache = val || {};
        return likedByMeCache;
      })
      .catch(() => {
        likedByMeCache = {};
        return likedByMeCache;
      });
  }
  return likedByMePromise;
}

async function incrementLike(productId) {
  // トランザクションの代わりにREST APIのtransactionを使用
  const res = await fetch(`${DB_URL}/likes/${productId}.json`);
  const current = (await res.json()) ?? 0;
  const next = current + 1;
  const putRes = await fetch(`${DB_URL}/likes/${productId}.json`, {
    method: 'PUT',
    body: JSON.stringify(next)
  });
  if (!putRes.ok) {
    const detail = await putRes.text().catch(() => '');
    throw new Error(`いいね数の更新に失敗しました (status: ${putRes.status}) ${detail}`);
  }
  if (allLikesCache) allLikesCache[productId] = next; // キャッシュも更新
  return next;
}

async function decrementLike(productId) {
  const res = await fetch(`${DB_URL}/likes/${productId}.json`);
  const current = (await res.json()) ?? 0;
  const next = Math.max(current - 1, 0);
  const putRes = await fetch(`${DB_URL}/likes/${productId}.json`, {
    method: 'PUT',
    body: JSON.stringify(next)
  });
  if (!putRes.ok) {
    const detail = await putRes.text().catch(() => '');
    throw new Error(`いいね数の更新に失敗しました (status: ${putRes.status}) ${detail}`);
  }
  if (allLikesCache) allLikesCache[productId] = next; // キャッシュも更新
  return next;
}

async function setLiked(customerId, productId) {
  const res = await fetch(`${DB_URL}/liked/${customerId}/${productId}.json`, {
    method: 'PUT',
    body: JSON.stringify(true)
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`いいね済み状態の保存に失敗しました (status: ${res.status}) ${detail}`);
  }
  if (likedByMeCache) likedByMeCache[productId] = true; // キャッシュも更新
}

async function removeLiked(customerId, productId) {
  const res = await fetch(`${DB_URL}/liked/${customerId}/${productId}.json`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`いいね済み状態の削除に失敗しました (status: ${res.status}) ${detail}`);
  }
  if (likedByMeCache) delete likedByMeCache[productId]; // キャッシュも更新
}

async function initLikeButton(button) {
  const productId = button.dataset.productId;
  const countEl = button.querySelector(".like-count");

  if (!productId) {
    button.style.display = "none";
    return;
  }

  // いいね数を取得（全商品ぶんをまとめて1回だけ取得したキャッシュから参照）
  const [allLikes, likedByMe] = await Promise.all([
    fetchAllLikesOnce(),
    fetchLikedByMeOnce()
  ]);
  const count = allLikes[productId] || 0;
  countEl.textContent = formatCount(count);

  // Shopify未ログインならアラート
  if (!customerId) {
    button.disabled = false;
    button.onclick = () => {
      alert('いいねするにはログインが必要です。');
    };
    return;
  }

  // いいね済みか確認（自分のいいね一覧キャッシュから参照）
  const hasLiked = likedByMe[productId] === true;

  button.disabled = false;
  if (hasLiked) {
    button.classList.add("liked");
    button.onclick = () => handleUnlike(button, productId, customerId);
  } else {
    button.classList.remove("liked");
    button.onclick = () => handleLike(button, productId, customerId);
  }
}

async function handleLike(button, productId, customerId) {
  button.disabled = true;

  try {
    await setLiked(customerId, productId);
    const newCount = await incrementLike(productId);

    const countEl = button.querySelector(".like-count");
    countEl.textContent = formatCount(newCount);

    button.classList.add("liked");
    button.disabled = false;
    button.onclick = () => handleUnlike(button, productId, customerId);
  } catch (err) {
    console.error("いいね処理に失敗しました:", err);
    alert('いいねの保存に失敗しました。時間をおいて再度お試しください。');
    button.disabled = false;
  }
}

async function handleUnlike(button, productId, customerId) {
  button.disabled = true;

  try {
    await removeLiked(customerId, productId);
    const newCount = await decrementLike(productId);

    const countEl = button.querySelector(".like-count");
    countEl.textContent = formatCount(newCount);

    button.classList.remove("liked");
    button.disabled = false;
    button.onclick = () => handleLike(button, productId, customerId);
  } catch (err) {
    console.error("いいね取り消しに失敗しました:", err);
    button.disabled = false;
  }
}

// 既存のボタンを初期化
document.querySelectorAll(".like-button").forEach(initLikeButton);

// 動的追加されたボタンも初期化
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches?.(".like-button")) {
        initLikeButton(node);
      }
      node.querySelectorAll?.(".like-button").forEach(initLikeButton);
    });
  });
});

observer.observe(document.body, { childList: true, subtree: true });
